import {
  ConfigurableFocusTrap,
  ConfigurableFocusTrapFactory,
  LiveAnnouncer
} from '@angular/cdk/a11y';
import { Clipboard } from '@angular/cdk/clipboard';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import cronstrue from 'cronstrue';
import { concatWith, firstValueFrom } from 'rxjs';
import seedrandom from 'seedrandom';
import { DrawerInputConfig } from '../../../models/drawer-model';
import { TimezoneService } from '../../../service/timezone.service';
import { DRAWER_PLACEHOLDERS } from '../constants/drawerPlaceHolders';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { Toast, ToastComponent } from '../toast/toast.component';
import { DrawerService } from './drawer.service';
import { isTimeWithinOperationalHours } from './practice.helper';

export interface ModalModel {
  [key: string]: any;
}

@Component({
  selector: 'app-drawer',
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
})
export class DrawerComponent<T extends ModalModel> {
  drawerType: 'add' | 'edit' = 'add';
  isDrawerOpen: boolean = false;
  isSubmitLoading = false;
  isConfirmationModalVisible: boolean = false;
  isAlertModalVisible: boolean = false;
  isAlertModalConfirmed: boolean = false;
  isAlertModalCancelled: boolean = false;
  alertModalTitle: string = '';
  alertModalMessage: string = '';

  @Input() loadScheduleData: any = [];
  @Input() nonScheduledPracticeData: any = [];
  @Input() drawerModel!: T;
  @Input() drawerConfig!: DrawerInputConfig[];
  @Input() drawerModelName?: string;
  @Input() editDataURL!: string;
  @Input() AddDataURL!: string;
  @Input() ResetURL!: string;
  @Input() OnDemandLoadURL?: string;
  @Input() RestartAgentURL?: string;
  @Input() createDrawerTitle: string = '';
  @Input() updateDrawerTitle: string = '';
  @Input() hasTwoColumns?: boolean = false;
  @Input() removeLevel?: boolean = true;
  @Input() maxDrawerHeight?: string = 'max-h-[650px]';
  @Output() resetData: EventEmitter<void> = new EventEmitter<void>();
  @Output() afterSave: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(ToastComponent) toastComponent!: ToastComponent;
  @ViewChildren(DropdownComponent) dropdowns: QueryList<DropdownComponent>;
  @ViewChild('drawer', { static: false }) drawer: ElementRef<HTMLElement>;

  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();
  @Output() beforeSave: EventEmitter<void> = new EventEmitter<void>();
  @Output() modelChange: EventEmitter<T> = new EventEmitter<T>();
  containsDuplicate: boolean = false;
  selectedSection: string | null = null;
  warningMessage: string = '';
  private seeded = false;

  @HostListener('document:keydown.escape', ['$any($event)']) onKeydownHandler(
    event: KeyboardEvent
  ) {
    if (this.isDrawerOpen) {
      this.closeDrawer();
    }
  }

  ngAfterViewInit() {
    this.dropdowns.changes.subscribe(() => {
      this.dropdowns.forEach((dropdown) => {
        dropdown.dependentDropDownChange.subscribe((event: any) => {
          this.onDependentDropDownChange(event);
        });
      });
    });

    const sections = this.getSections(this.drawerConfig);
    if (sections.length > 0) {
      this.selectedSection = sections[0];
      this._changeDetectorRef.detectChanges();
    }
  }

  onSectionSelect(section: string): void {
    this.selectedSection = section;
  }

  hasSectionConfig(): boolean {
    return this.drawerConfig?.some(c => !!c.section);
  }

  getSections(config: DrawerInputConfig[]): string[] {
    if (!config) return [];
    const sections = config
      .map(x => x.section)
      .filter((s): s is string => !!s);
    // Remove duplicates
    return Array.from(new Set(sections));
  }

  getSectionItems(section: string): DrawerInputConfig[] {
    return this.drawerConfig.filter(x => x.section === section);
  }

  getFieldValue(jsonKey: string): any {
    if (this.drawerModel[jsonKey] === 'true' || this.drawerModel[jsonKey] === true)
      return "Yes";
    else if (this.drawerModel[jsonKey] === 'false' || this.drawerModel[jsonKey] === false)
      return "No";
    return this.drawerModel[jsonKey];
  }

  getSectionWidth(section?: string): string {
    const items = section
      ? this.drawerConfig.filter(x => x.section === section)
      : this.drawerConfig;
    return items.length === 1 ? 'w-[660px]' : 'w-[350px]';
  }

  public get focusTrapFactory(): ConfigurableFocusTrapFactory {
    return this._focusTrapFactory;
  }
  public set focusTrapFactory(value: ConfigurableFocusTrapFactory) {
    this._focusTrapFactory = value;
  }
  public get changeDetectorRef(): ChangeDetectorRef {
    return this._changeDetectorRef;
  }
  public set changeDetectorRef(value: ChangeDetectorRef) {
    this._changeDetectorRef = value;
  }
  focusTrap: ConfigurableFocusTrap = null;
  requiredClass = "after:content-['*'] after:ml-0.5 after:text-red-500";
  addOnBlur = true;
  showValidators = false;
  showCertificateInput = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  showPassword: boolean = false;
  disableLicenseKey: boolean = false;

  constructor(
    private drawerService: DrawerService,
    private announcer: LiveAnnouncer,
    private _focusTrapFactory: ConfigurableFocusTrapFactory,
    private _changeDetectorRef: ChangeDetectorRef,
    private clipboard: Clipboard,
    private timezoneService: TimezoneService,
  ) { }

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
  }

  shouldShowPracticeDrawerActions(): boolean {
    return (
      this.drawerType === 'edit' &&
      this.drawerModelName === 'practiceMasterData' &&
      this.updateDrawerTitle === 'Update Practice'
    );
  }

  getTimeFromCron(cron: string): string | null {
    const parts = cron.trim().split(' ');
    if (parts.length < 3) return null;

    const hour = parts[2];
    const minute = parts[1];

    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }

  async saveChanges() {
    if (this.drawerType === 'edit' && this.drawerModelName === 'practiceMasterData') {
      if (this.drawerModel['connectionString'] === null || this.drawerModel['connectionString'] === '') {
        this.drawerModel = { ...this.drawerModel, connectionString: null } as T;
      } else {
        this.drawerModel = { ...this.drawerModel, newDatabaseServerName: null, newDatabaseName: null } as T;
      }
    }

    // Transform DayOfWeek dropdown object back to numeric value before submission
    if (this.drawerModelName === 'practiceOperationalHoursData') {
      const dayConfig = this.drawerConfig.find(c => c.uniqueIdentifier === 'DayOfWeek');
      if (dayConfig && typeof this.drawerModel[dayConfig.jsonKey] === 'object' && this.drawerModel[dayConfig.jsonKey]?.id) {
        this.drawerModel = { ...this.drawerModel, [dayConfig.jsonKey]: this.drawerModel[dayConfig.jsonKey].id } as T;
      }
    }

    // Strip trailing semicolons from query fields (PMS Object only)
    if (this.drawerModelName === 'pmsObject') {
      this.drawerConfig.forEach(cfg => {
        if (cfg.jsonKey === 'query' && typeof this.drawerModel[cfg.jsonKey] === 'string') {
          this.drawerModel = {
            ...this.drawerModel,
            [cfg.jsonKey]: this.drawerModel[cfg.jsonKey].trimEnd().replace(/;+$/, '').trimEnd()
          } as T;
        }
      });
    }

    this.showValidators = true;
    for (let element of this.drawerConfig) {
      // For required select fields with FK, block save if FK is NIL/null regardless of disabled state
      if (element.isRequired && element.input === 'select' && element.foreignKeyProperty) {
        if (this.isInvalidForeignKey(this.drawerModel[element.foreignKeyProperty])) return;
      }

      const value = this.validateInput(
        this.drawerModel[element.jsonKey],
        element.label,
        element.input,
        element.jsonKey,
        element?.minLength,
        element?.maxLength,
        element?.isRequired,
        element.validationRegex
      );

      if (value) {
        return;
      }
    }
    this.isSubmitLoading = true;
    if (this.drawerType === 'edit') {
      const editURL = this.editDataURL.replace(
        ':id',
        this.drawerModel['id']
      );
      this.drawerService
        .editEntry(this.drawerModel, editURL)
        .subscribe({
          next: (value: any) => {
            this.isSubmitLoading = false;
            this.closeDrawer();
            this.showToast({
              severity: 'success',
              title: 'Success',
              message: 'Record Updated Successfully',
            });
            this.refreshGrid();
            this.afterSave.emit();
          },
          error: (err) => {
            this.isSubmitLoading = false;

            this.showToast({
              severity: 'error',
              title: 'Error',
              message: err.error.errorMessage,
            });
          },
          complete: () => { },
        });
    } else {
      this.beforeSave.emit();
      this.drawerService.addEntry(this.drawerModel, this.AddDataURL).subscribe({
        next: (res: any) => {
          this.isSubmitLoading = false;
          this.closeDrawer();
          this.showToast({
            severity: 'success',
            title: 'Success',
            message: 'Record added succesfully',
          });
          this.refreshGrid();
          this.afterSave.emit();
        },
        error: (err: any) => {
          this.isSubmitLoading = false;
          this.showToast({
            severity: 'error',
            title: 'Error',
            message: err.error.errorMessage,
          });
        },
      });
    }
  }

  async onResetConfirm() {
    if (this.drawerType === 'edit' && this.drawerModelName === 'practiceMasterData') {
      const resetURL = this.ResetURL.replace(
        ':id',
        this.drawerModel['id']
      );
      this.isSubmitLoading = true;
      this.beforeSave.emit();
      this.drawerService.addEntry(null, resetURL).subscribe({
        next: (res: any) => {
          this.isSubmitLoading = false;
          this.closeDrawer();
          this.showToast({
            severity: 'success',
            title: 'Success',
            message: 'Successfully reset the installation for the practice',
          });
          this.refreshGrid();
          this.afterSave.emit();
        },
        error: (err: any) => {
          this.isSubmitLoading = false;
          this.showToast({
            severity: 'error',
            title: 'Error',
            message: err.error.errorMessage,
          });
        },
      });
    }
  }

  async onOnDemandLoadConfirm() {
    if (this.drawerType === 'edit' && this.drawerModelName === 'practiceMasterData') {
      this.isSubmitLoading = true;
      this.beforeSave.emit();
      const payload = {
        PracticeId: this.drawerModel['id'],
        PracticeCode: this.drawerModel['practiceCode'],
        ActionByUser: 'The on-Demand load started from the Admin UI by ' + localStorage.getItem('userEmail'),
        Action: 'START_ON_DEMAND_LOAD',
        ActionParameters: ''
      };

      this.drawerService.addEntry(payload, this.OnDemandLoadURL).subscribe({
        next: (res: any) => {
          this.isSubmitLoading = false;
          this.closeDrawer();
          this.showToast({
            severity: 'success',
            title: 'Success',
            message: 'The on-demand load message is sent to the practice.',
          });
          this.refreshGrid();
          this.afterSave.emit();
        },
        error: (err: any) => {
          this.isSubmitLoading = false;
          this.showToast({
            severity: 'error',
            title: 'Error',
            message: err.error.errorMessage,
          });
        },
      });
    }
  }

  async onRestartAgentConfirm() {
    if (this.drawerType === 'edit' && this.drawerModelName === 'practiceMasterData') {
      this.isSubmitLoading = true;
      this.beforeSave.emit();
      const payload = {
        PracticeId: this.drawerModel['id'],
        PracticeCode: this.drawerModel['practiceCode'],
        ActionByUser: 'The Agent Services restarted from the Admin UI by ' + localStorage.getItem('userEmail'),
        Action: 'RESTART_AGENT_SERVICES',
        ActionParameters: ''
      };

      this.drawerService.addEntry(payload, this.RestartAgentURL || this.OnDemandLoadURL).subscribe({
        next: (res: any) => {
          this.isSubmitLoading = false;
          this.closeDrawer();
          this.showToast({
            severity: 'success',
            title: 'Success',
            message: 'Agent restart services message sent to the practice.',
          });
          this.refreshGrid();
          this.afterSave.emit();
        },
        error: (err: any) => {
          this.isSubmitLoading = false;
          this.showToast({
            severity: 'error',
            title: 'Error',
            message: err.error.errorMessage,
          });
        },
      });
    }
  }

  // For Practice Load Configuration ONLY
  async validatePracticeLoadConfiguration() {
    const selectedLoadSCheduleName = this.drawerModel['loadScheduleName'];
    var selectedPracticeCode = '';

    this.dropdowns.forEach((dropdown) => {
      if (dropdown.uniqueIdentifier === 'NonSchedulePracticeCode') {
        this.nonScheduledPracticeData = dropdown.nonScheduledPracticeData;
      }
      if (dropdown.uniqueIdentifier === 'LoadScheduleName') {
        this.loadScheduleData = dropdown.loadScheduleData;
      }
    });

    if (this.drawerType === 'edit')
      selectedPracticeCode = this.drawerModel['practiceCode'] || '';
    else {
      selectedPracticeCode = this.drawerModel['practiceName'].split(' ')[0] || '';
    }
    
    // Extract just the practice code if it's in 'PracticeCode (PMSName)' format
    if (selectedPracticeCode.includes('(')) {
      selectedPracticeCode = selectedPracticeCode.split(' ')[0];
    }

    if (selectedPracticeCode === '' || selectedPracticeCode === 'Select' || selectedLoadSCheduleName === '')
      return;

    var selectedPractice: any = null;
    
    // Try to find in nonScheduledPracticeData first
    selectedPractice = this.nonScheduledPracticeData.find(item =>
      item.practiceCode === selectedPracticeCode);
    
    // If not found, get from the dropdown's full practice data (for edit mode)
    if (!selectedPractice) {
      const practiceDropdown = this.dropdowns.find(
        (dropdown) => dropdown.uniqueIdentifier === 'NonSchedulePracticeCode'
      );
      if (practiceDropdown && practiceDropdown.nonScheduledPracticeData) {
        selectedPractice = practiceDropdown.nonScheduledPracticeData.find(
          (item: any) => item.practiceCode === selectedPracticeCode
        );
      }
    }
    
    if (!selectedPractice) {
      console.warn('Practice not found:', selectedPracticeCode);
      return;
    }

    const selectedLoadSchedule = this.loadScheduleData.filter(item =>
      item.name === selectedLoadSCheduleName)[0];

    const selectedTimeZone = selectedPractice.timezone;
    const selectedCronExpression = selectedLoadSchedule.startCronExpression || '';
    const time = this.getTimeFromCron(selectedCronExpression) || '00:00';

    const rawOperationalHoursData = selectedPractice.practiceOperationalHours || [];
    const parsedHours = Array.isArray(rawOperationalHoursData)
      ? rawOperationalHoursData.map((item: any) => ({
        day: Number(item.day),
        openingTime: String(item.openingTime || ''),
        closingTime: String(item.closingTime || ''),
        isClosed: Boolean(item.isClosed)
      }))
      : [];

    // Alter for empty operational hours
    if (parsedHours.length <= 0) {
      const confirmed = await this.showConfirmationModal(
        'Are you sure?',
        'No operational hours data found for the selected practice. Do you want to proceed?'
      );

      if (!confirmed) {
        this.isAlertModalCancelled = true;
        return;
      } else {
        this.isAlertModalConfirmed = true;
      }

      this.isAlertModalVisible = false;
    }

    // Alert for operational hours
    if (!this.isAlertModalConfirmed) {
      const timezones = await firstValueFrom(this.timezoneService.getTimezones());
      const isOpen = isTimeWithinOperationalHours(time, selectedTimeZone, parsedHours, timezones);

      if (isOpen) {
        const confirmed = await this.showConfirmationModal(
          'Are you sure?',
          'Practice is open at the selected scheduled time. Do you still want to proceed?'
        );
        if (!confirmed) {
          this.isAlertModalCancelled = true;
          return;
        } else {
          this.isAlertModalConfirmed = true;
        }

        this.isAlertModalVisible = false;
      }
      // });
    }
  }

  closeDrawer() {
    this.dropdowns?.forEach((dropdown) => (dropdown.showDropdown = false));
    this.isDrawerOpen = false;
    this.showValidators = false;
    this.focusTrap._disable();
    this.onResetClick();
    this.disableLicenseKey = false;
  }

  openDrawer() {
    if (
      this.drawerType == 'edit' &&
      this.drawerModel['licenseKey'] &&
      this.drawerModel['isInstalled']
    ) {
      this.disableLicenseKey = true;
    }
    this.focusTrap = this._focusTrapFactory.create(
      this.drawer.nativeElement,
      {
        defer: true,
      }
    );
    this.focusTrap.focusFirstTabbableElement();
    this.isDrawerOpen = true;
    this.showValidators = false;

    if (this.drawerType === 'edit') {
      // Simulate the selection of the parent dropdown during edit
      this.drawerConfig.forEach((config) => {
        if (config.dependentDropDownConfig) {
          const selectedOption = this.getSelectedOption(
            config.jsonKey,
            config.uniqueIdentifier
          );
          if (selectedOption) {
            // Trigger the dependent dropdown change event
            this.onDependentDropDownChange({
              key: config.jsonKey,
              value: selectedOption.id,
            });
          }
        }
      });
    }
  }

  logSelectedOptions() {
    this.drawerConfig.forEach((config) => {
      if (config.input === 'select') {
        const selectedOption = this.getSelectedOption(
          config.jsonKey,
          config.uniqueIdentifier
        );
        if (selectedOption) { } else { }
      }
    });
  }

  getSelectedOption(
    key: string,
    uniqueIdentifier: string
  ): { id: any; name: any; uniqueIdentifier: string } | null {
    const selectedValue = this.drawerModel[key];
    const foreignKeyProperty = this.drawerConfig.find(
      (config) => config.jsonKey === key
    )?.foreignKeyProperty;

    if (selectedValue && foreignKeyProperty) {
      const selectedId = this.drawerModel[foreignKeyProperty];
      return {
        id: selectedId,
        name: selectedValue,
        uniqueIdentifier: uniqueIdentifier,
      };
    }

    return null;
  }

  refreshGrid() {
    this.refresh.emit();

    // Upddate NonSchedulePracticeCode dropdown
    if (this.drawerModelName === 'practiceLoadConfiguration') {
      this.dropdowns.forEach((dropdown) => {
        if (dropdown.uniqueIdentifier === 'NonSchedulePracticeCode') {
          dropdown.fetchOptions();
        }
      });
    }
  }

  getDropdownValue(config: DrawerInputConfig): any {
    if (config.foreignKeyProperty) {
      return {
        name: this.drawerModel[config.jsonKey],
        id: this.drawerModel[config.foreignKeyProperty]
      };
    }

    const currentValue = this.drawerModel[config.jsonKey];
    if (currentValue && typeof currentValue === 'object') {
      return currentValue;
    }

    // Plain string value — wrap into {id, name} so dropdown can display it
    if (currentValue && typeof currentValue === 'string') {
      return { id: currentValue, name: currentValue };
    }

    if (config.options?.length) {
      return config.options.find(
        option => option.id === currentValue || option.name === currentValue
      ) ?? currentValue;
    }

    return currentValue;
  }

  updateDrawerModel(jsonKey: string, foreignKeyProperty: any, value: any) {
    const newDrawerModel: { [key: string]: any } = { ...this.drawerModel };
    // When a field that others conditionally depend on changes, clear those fields
    this.drawerConfig.forEach(cfg => {
      if (cfg.conditionalRequired?.dependsOn === jsonKey) {
        const incomingName = typeof value === 'object' && value !== null ? value.name : value;
        if (incomingName === cfg.conditionalRequired.requiredWhenNot) {
          newDrawerModel[cfg.jsonKey] = null;
          if (cfg.foreignKeyProperty) {
            newDrawerModel[cfg.foreignKeyProperty] = null;
          }
        }
      }
    });

    if (typeof value === 'object' && value !== null) {
      if (foreignKeyProperty) {
        // Standard dropdown with foreign key
        newDrawerModel[jsonKey] = value.name;
        newDrawerModel[`${foreignKeyProperty}`] = value.id;
      } else {
        // Special case for DayOfWeek dropdown - store ID directly
        const config = this.drawerConfig.find(c => c.jsonKey === jsonKey);
        if (config?.uniqueIdentifier === 'DayOfWeek') {
          newDrawerModel[jsonKey] = value.id;
          newDrawerModel['dayName'] = value.name;
        } else {
          // For other dropdowns without foreign key, store name (existing behavior)
          newDrawerModel[jsonKey] = value.name;
        }
      }
      // Trigger the dependent dropdown change event
      const dependentDropDownConfig = this.drawerConfig.find(
        (config) => config.jsonKey === jsonKey
      )?.dependentDropDownConfig;
      if (dependentDropDownConfig) {
        this.onDependentDropDownChange({
          key: jsonKey,
          value: value.id,
        });
      }
    } else {
      newDrawerModel[jsonKey] = value;
    }

    this.drawerModel = newDrawerModel as T;
    this.modelChange.emit(this.drawerModel);
  }

  addChip(key: string, event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      const chips = this.drawerModel[key]
        ? this.drawerModel[key].split(',')
        : [];
      const lowercaseChips = chips.map((chip) => chip.toLowerCase());
      this.resetDuplicate();
      if (lowercaseChips.includes(value.toLowerCase())) {
        this.containsDuplicate = true;
        event.chipInput!.clear();

        return;
      }

      chips.push(value);
      this.drawerModel = {
        ...this.drawerModel,
        [key]: chips.join(','),
      };
    }

    event.chipInput!.clear();
  }

  resetDuplicate() {
    this.containsDuplicate = false;
  }

  removeChip(key: string, chip: string): void {
    const chips = this.drawerModel[key].split(',');
    const index = chips.indexOf(chip);

    if (index >= 0) {
      chips.splice(index, 1);
      this.drawerModel = {
        ...this.drawerModel,
        [key]: chips.join(','),
      };
      this.announcer.announce(`Removed ${chip}`);
    }
  }

  editChip(key: string, chip: string, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.removeChip(key, chip);
      return;
    }

    const chips = this.drawerModel[key].split(',');
    const index = chips.indexOf(chip);

    if (index >= 0) {
      this.resetDuplicate();
      const lowercaseChips = chips.map((c) => c.toLowerCase());
      const lowercaseValue = value.toLowerCase();

      const isDuplicate = lowercaseChips.some(
        (c, i) => c === lowercaseValue && i !== index
      );

      if (isDuplicate) {
        this.containsDuplicate = true;
        this.drawerModel = {
          ...this.drawerModel,
          [key]: chips.join(','),
        };
        return;
      }

      chips[index] = value;
      this.drawerModel = {
        ...this.drawerModel,
        [key]: chips.join(','),
      };
    }
  }

  // method to return error message for input

  validateInput(
    value: string,
    label: string,
    input: string,
    key?: string,
    minLength?: number,
    maxLength?: number,
    isRequired?: boolean,
    regex?: RegExp
  ): string {

    // if field isn't required then return empty message
    if (!isRequired) {
      return '';
    }

    // Check conditionalRequired: skip required validation if the condition is not met
    try {
      const fieldConfig = this.drawerConfig.find((c: any) => c.jsonKey === key);
      if (fieldConfig?.conditionalRequired) {
        const { dependsOn, requiredWhenNot } = fieldConfig.conditionalRequired;
        const dependsOnValue = this.drawerModel[dependsOn];
        if (dependsOnValue === requiredWhenNot) {
          return '';
        }
      }
    } catch (err) {
      console.warn('validateInput: conditionalRequired check failed', err);
    }

    try {
      const hasSections = Array.isArray(this.drawerConfig) && this.drawerConfig.some((c: any) => !!c.section);
      if (hasSections) {
        if (!this.selectedSection) {
          return '';
        }

        const fieldConfig = this.drawerConfig.find((c: any) => c.jsonKey === key);
        if (fieldConfig && fieldConfig.section !== this.selectedSection) {
          value = null;
          return '';
        }
      }
    } catch (err) {
      console.warn('validateInput: Section validation fallback triggered', err);
    }

    // validate in case of select
    if (
      input === 'select' &&
      (value === DRAWER_PLACEHOLDERS.SELECT_TYPE ||
        value === DRAWER_PLACEHOLDERS.SELECT_PMS ||
        value === DRAWER_PLACEHOLDERS.SELECT_OBJECT ||
        value === DRAWER_PLACEHOLDERS.SELECT_PRACTICE ||
        value === DRAWER_PLACEHOLDERS.SELECT_LOAD_SCHEDULE ||
        value === DRAWER_PLACEHOLDERS.SELECT_STATUS ||
        value === DRAWER_PLACEHOLDERS.SELECT_ROLE ||
        value === DRAWER_PLACEHOLDERS.SELECT_USER ||
        value === DRAWER_PLACEHOLDERS.DATABASESERVERTYPE ||
        value === DRAWER_PLACEHOLDERS.SELECT_PMSNAME ||
        value === DRAWER_PLACEHOLDERS.SELECT_AGENTVERSION ||
        value === DRAWER_PLACEHOLDERS.SELECT_AGENTRELEASE ||
        value === DRAWER_PLACEHOLDERS.SELECT_LOADTYPE)
    ) {
      return 'Please select an option from the dropdown.';
    }

    // validate in case of text or passowrd
    else if (input === 'text' || input === 'password') {
      if (!value) {
        return 'This field is required. Please provide input.';
      } else if (regex && !regex.test(value)) {
        if (key === 'cronExpression') {
          return 'Please enter a valid cron expression.';
        }
        return 'This field requires a valid input. Please check the format and try again.';
      } else if (
        (value && value.length < minLength) ||
        (value && value.length > maxLength)
      ) {
        return `Input must be ${minLength} to ${maxLength} characters long.`;
      }
    }

    // validate in case of number
    else if (input === 'number') {
      return !+value
        ? 'This field is required.'
        : +value < minLength || +value > maxLength
          ? `Please enter a value between ${minLength} and ${maxLength}`
          : '';
    } else if (input === 'date') {
      if (!value) {
        return 'This field is required. Please provide input.';
      }
    }

    // validate in case of textarea
    else if (input === 'textarea') {
      if (!value) {
        return 'This field is required. Please provide input.';
      } else if (
        (value && value.length < minLength) ||
        (value && value.length > maxLength)
      ) {
        return `Input must be ${minLength} to ${maxLength} characters long.`;
      }
    } else if (input === 'time') {
      if (!value) {
        return 'This field is required. Please provide input.';
      }
    }
    // return empty message if no validation is required
    return '';
  }

  getDayName(day: number): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day];
  }

  isInputValidated(): boolean {
    return false;
  }

  private isInvalidForeignKey(fkValue: any): boolean {
    return !fkValue || fkValue === '00000000-0000-0000-0000-000000000000' || fkValue === 0;
  }

  hasInvalidFields(): boolean {
    if (!this.drawerModel || !this.drawerConfig) return false;
    if (this.dropdowns?.some(d => d.isLoading && !d.disabled)) return true;
    return this.drawerConfig.some(element => {
      // For required select fields with a foreign key, always validate the FK — even if the UI is disabled
      if (element.isRequired && element.input === 'select' && element.foreignKeyProperty) {
        if (this.isInvalidForeignKey(this.drawerModel[element.foreignKeyProperty])) return true;
      }

      // Skip non-FK disabled fields from further validation
      if (this.isFieldConditionallyDisabled(element)) return false;

      return !!this.validateInput(
        this.drawerModel[element.jsonKey],
        element.label,
        element.input,
        element.jsonKey,
        element?.minLength,
        element?.maxLength,
        element?.isRequired,
        element.validationRegex
      );
    });
  }

  isFieldConditionallyRequired(item: DrawerInputConfig): boolean {
    if (!item.isRequired) return false;
    if (!item.conditionalRequired) return true;
    const { dependsOn, requiredWhenNot } = item.conditionalRequired;
    return this.drawerModel[dependsOn] !== requiredWhenNot;
  }

  isFieldConditionallyDisabled(item: DrawerInputConfig): boolean {
    if (item.isDisabled) return true;
    if (!item.conditionalRequired) return false;
    const { dependsOn, requiredWhenNot } = item.conditionalRequired;
    return this.drawerModel[dependsOn] === requiredWhenNot;
  }

  onDependentDropDownChange(event: any) {
    const { key, value } = event;

    const dependentDropDownConfig = this.drawerConfig.find(
      (config) => config.jsonKey === key
    )?.dependentDropDownConfig;

    if (dependentDropDownConfig) {
      const url = `${dependentDropDownConfig.dependentDropDownURL}?${dependentDropDownConfig.dependentDropDownParam}=${value}`;
      const dependentDropDown = this.dropdowns.find(
        (dropdown) =>
          dropdown.uniqueIdentifier ===
          dependentDropDownConfig.dependentDropDownKey
      );
      if (!dependentDropDown) return;
      dependentDropDown.isLoading = true;
      this.drawerService.getOptions(url).subscribe(
        (response) => {
          if (response.isSuccess && response.data) {

            // Special- Practice Load Configuration
            if (this.drawerModelName !== null && this.drawerModelName === 'practiceLoadConfiguration') {
              if (url.includes('LoadSchedule/GetAllSchedules')) {
                this.loadScheduleData = response.data;
              }
              if (url.includes('Practice/GetNonScheduledPractice')) {
                this.nonScheduledPracticeData = response.data;
              }
            }

            if (dependentDropDown) {
              const previouslySelectedOption = this.getSelectedOption(
                dependentDropDownConfig.dependentDropDownKey,
                dependentDropDown.uniqueIdentifier
              );

              dependentDropDown.options = response.data
                .map((item: any) => ({
                  id: item.id,
                  name: item[dependentDropDown.dropDownJsonKey || 'name'],
                  sequenceId: item.sequenceId
                }))
                .sort((a, b) => {
                  if (dependentDropDown.uniqueIdentifier === 'LoadScheduleName') {
                    const aSeq = a.sequenceId ?? 0;
                    const bSeq = b.sequenceId ?? 0;
                    return aSeq - bSeq;
                  }
                  return (a.name || '').localeCompare(b.name || '');
                });
              dependentDropDown['allOptions'] = [...dependentDropDown.options];
              dependentDropDown.isLoading = false;
              // Set the previously selected option if it exists in the updated options
              if (previouslySelectedOption) {
                const selectedOption = dependentDropDown.options.find(
                  (option) => option.id === previouslySelectedOption.id
                );
                if (selectedOption) {
                  this.updateDrawerModel(
                    dependentDropDownConfig.dependentDropDownKey,
                    dependentDropDown.foreignKeyProperty,
                    selectedOption
                  );
                } else {
                  this.updateDrawerModel(
                    dependentDropDownConfig.dependentDropDownKey,
                    dependentDropDown.foreignKeyProperty,
                    null
                  );
                }
              } else {
                this.updateDrawerModel(
                  dependentDropDownConfig.dependentDropDownKey,
                  dependentDropDown.foreignKeyProperty,
                  null
                );
              }

              this._changeDetectorRef.detectChanges();
            }
          }
        },
        (error) => {
          console.error(`Error fetching options for ${key}:`, error);
        }
      );
    }
  }

  certificateInput(value: boolean) {
    this.showCertificateInput = value;
  }

  onResetClick() {
    this.warningMessage = '';
    this.showValidators = false;
    this.resetData.emit();
  }

  async onResetInstallationClick() {
    if (this.drawerModelName !== null && this.drawerModelName === 'practiceMasterData') {
      const confirmed = await this.showConfirmationModal(
        'Are you sure?',
        'You want to reset the installation for this practice?'
      );

      if (!confirmed) {
        this.isAlertModalVisible = false;
        this.focusTrap._disable();
        return;
      } else {
        this.isAlertModalVisible = false;
        this.onResetConfirm();
      }
    }
  }

  async onOnDemandLoadClick() {
    if (this.drawerModelName !== null && this.drawerModelName === 'practiceMasterData') {
      const confirmed = await this.showConfirmationModal(
        'Are you sure?',
        'You want to start an on-demand load for this practice?'
      );

      if (!confirmed) {
        this.isAlertModalVisible = false;
        this.focusTrap._disable();
        return;
      } else {
        this.isAlertModalVisible = false;
        await this.onOnDemandLoadConfirm();
      }
    }
  }

  async onRestartAgentClick() {
    if (this.drawerModelName !== null && this.drawerModelName === 'practiceMasterData') {
      const confirmed = await this.showConfirmationModal(
        'Are you sure?',
        'You want to restart the Agent Services for this practice?'
      );

      if (!confirmed) {
        this.isAlertModalVisible = false;
        this.focusTrap._disable();
        return;
      } else {
        this.isAlertModalVisible = false;
        this.onRestartAgentConfirm();
      }
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  generateKey(jsonKey: string, event: Event): void {
    event.preventDefault();
    // Seed the random number generator
    if (!this.seeded) {
      this.seeded = true;
      seedrandom(Date.now().toString());
    }

    // Generate XY (10-99), Z (0-9), W (10-99)
    const XY = Math.floor(Math.random() * 90) + 10;
    const Z = Math.floor(Math.random() * 10);
    const W = Math.floor(Math.random() * 90) + 10;

    // Calculate ABCD based on the formula (XY + Z) * W + 9 = ABCD / 4
    const intermediate = (XY + Z) * W + 9;
    let ABCD = Math.floor((intermediate * 4) / 17);

    // Ensure ABCD is a two-digit number (10-99)
    ABCD = (ABCD % 90) + 10;

    // Create the key as a 7-digit string
    const key =
      String(XY).padStart(2, '0') +
      String(Z).padStart(1, '0') +
      String(W).padStart(2, '0') +
      String(ABCD).padStart(2, '0');

    // Create the key as a 7-digit string
    this.drawerModel = {
      ...this.drawerModel,
      [jsonKey]: key,
    };
    this.clipboard.copy(key);

    // Show toast notification
    this.showToast({
      severity: 'success',
      title: 'Copied to clipboard',
      message: 'License Key Generated and Copied',
    });

    // Trigger change detection
    this._changeDetectorRef.detectChanges();
  }

  copyToClipboard(text: string, event?: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.clipboard.copy(text);
    this.showToast({
      severity: 'success',
      title: 'Copied',
      message: 'License key copied to clipboard',
    });
  }

  onLicenseKeyEnter(jsonKey: string, event: KeyboardEvent) {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar'
    ) {
      event.preventDefault();
      this.generateKey(jsonKey, event);
    }
  }

  disableFieldsOnToggle(fieldsToDisableOnToggle: string[], value: boolean) {
    value
      ? this.drawerConfig
        .filter((e) => fieldsToDisableOnToggle?.includes(e.jsonKey))
        .map(
          (e) => (
            (e.isDisabled = true),
            (this.drawerModel = { ...this.drawerModel, [e.jsonKey]: '' })
          )
        )
      : this.drawerConfig
        .filter((e) => fieldsToDisableOnToggle?.includes(e.jsonKey))
        .map((e) => (e.isDisabled = false));
    this._changeDetectorRef.detectChanges();
  }

  // method listens every input changes
  onValueChage($event: Event, jsonKey: string) {
    if (jsonKey === 'startCronExpression') {
      const target = $event.target as HTMLInputElement;
      // extracting CRON from Input
      const cronExpression = target.value;
      let translatedCRON = '';
      try {
        translatedCRON = cronstrue.toString(cronExpression);
      } catch {
        translatedCRON = 'Invalid CRON Expression';
      }
      this.drawerModel = {
        ...this.drawerModel,
        ['description']: translatedCRON,
      };
    }
  }

  handleModalAction(action: 'confirm' | 'cancel') {
    this.isConfirmationModalVisible = false;
    if (action === 'confirm') {
      this.saveChanges();
    }

    this.focusTrap = this._focusTrapFactory.create(
      this.drawer.nativeElement,
      {
        defer: true,
      }
    );
    this.focusTrap.focusFirstTabbableElement();
  }

  handleAlertModalAction(action: 'confirm' | 'cancel') {
    this.isAlertModalVisible = false;
    if (action === 'confirm') {
      this.isAlertModalConfirmed = true
    } else {
      this.isAlertModalConfirmed = false;
    }

    this.focusTrap = this._focusTrapFactory.create(
      this.drawer.nativeElement,
      {
        defer: true,
      }
    );
    this.focusTrap.focusFirstTabbableElement();
  }

  async openConfirmationModal() {
    this.isAlertModalCancelled = false;
    this.isAlertModalConfirmed = false;
    if (this.drawerModelName !== null && this.drawerModelName === 'practiceLoadConfiguration') {
      await this.validatePracticeLoadConfiguration();

      if (this.isAlertModalCancelled)
        return;

      if (this.drawerType === 'edit' && this.isAlertModalConfirmed === false && this.isAlertModalCancelled === false) {
        this.isConfirmationModalVisible = true;
        this.focusTrap._disable();
      } else {
        this.saveChanges();
      }

      return;
    }

    // if updating show confirm modal
    if (this.drawerType === 'edit') {
      this.isConfirmationModalVisible = true;
      this.focusTrap._disable();
    } else {
      this.saveChanges();
    }
  }

  showConfirmationModal(title: string, message: string): Promise<boolean> {
    this.alertModalTitle = title;
    this.alertModalMessage = message;
    this.isAlertModalVisible = true;

    return new Promise((resolve) => {
      const originalHandler = this.handleAlertModalAction.bind(this);
      this.handleAlertModalAction = (action: 'confirm' | 'cancel') => {
        this.isAlertModalVisible = false;
        resolve(action === 'confirm');
        // Restore the original handler to avoid memory leaks
        this.handleAlertModalAction = originalHandler;
      };
    });
  }
}
